import { useContext } from 'react'
import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { isEmpty } from 'lodash-es'
import { useRef } from 'react'
import { useAppSelector } from '@/redux/hooks'

// Libs
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import getDisplayPrice from '@/methods/getDisplayPrice'
import { handleGtmSelectItem } from '@/methods/handleGtmEvent'
import { isClientSideShopApp } from '@/utils/navigator'
import { useObserve } from '@/hooks/useObserver'
import useLiveImpression from '@/hooks/useLiveImpression'
import { LIVE_TRACK_EVENT, trackLiveEvent } from '@/methods/liveTracking'

// Components
import Image from '@/components/common/Image'
import Icon from '@/assets/icons'
import DiscountRateTag from '@/components/shared/DiscountRateTag'
import { toast } from 'react-toastify'
import AdaptiveButton, { AdaptiveButtonTheme } from '@/components/common/AdaptiveButton'
import Button from '@/components/common/Button'
import Carousell from '@/components/shared/livemallclub/ProductCard/components/Carousell'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'
import { VALID_BRAND_NAME } from '@/constants/brand'
import { isLiveMallClub } from '@/constants/systemType'

// Methods
import { hasDoubleEleven } from '@/methods/badge/hasDoubleEleven'
import useMedia from '@/hooks/useMedia'

// Types
import { IRoomProductItem } from '@/types/room'
import { ProductInfoType } from '@/api/fetchProductData'

// Contexts
import { LiveRoomContext } from '@/components/pages/LiveRoom/index'

// Styles
import styles from './style.module.scss'
import { pxecPostMessages } from '@/utils/pxec/postMessages'
import { usePathname, useSearchParams } from 'next/navigation'
import { useMutation } from 'react-query'
import { countProductClick } from '@/api/countProductClick'
import { safeUrl } from '@/methods/url/safeUrl'

const cx = classnames.bind(styles)

interface ITagItem {
  style: number
  name: string
}

type RoomProductProps = {
  className?: string
  productInfo: IRoomProductItem
  productUrl?: string
  sortName: string
  isLive: boolean
  isTop: boolean
  isDel: boolean
  handleAddCartClick: (event: any, productId: string) => void
  handleBuyNowClick: (event: any, productId: string) => void
  observer?: IntersectionObserver
  gtmItemEventParams?: {
    itemListName: string
    itemListId?: string
  }
  pipWithWinOpen: (url: string) => void
  handlePromotionMessage: any
  liveRoomId?: string // 直播行為事件用
}

const Tag = (props: ITagItem) => {
  return (
    <label
      className={cx('product__tag', {
        'product__tag--style-0': props.style === 0,
        'product__tag--style-1': props.style === 1,
        'product__tag--style-2': props.style === 2,
      })}
    >
      <span className={cx('product__tag-name')}>{props.name}</span>
    </label>
  )
}

const ActionButton = ({
  className,
  onClick,
  theme,
  children,
}: {
  className: string
  onClick: (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void
  theme?: AdaptiveButtonTheme
  children: React.ReactNode
}) => {
  if (isLiveMallClub)
    return (
      <AdaptiveButton className={className} onClick={onClick} theme={theme}>
        {children}
      </AdaptiveButton>
    )
  return (
    <div className={className} onClick={onClick}>
      {children}
    </div>
  )
}

function getBrandButtonText(brandName: string, t: (key: string) => string) {
  switch (brandName) {
    case VALID_BRAND_NAME.ACMILAN:
    case VALID_BRAND_NAME.FIVE_STAR:
      return t('Brand_Live_Submit_Application')
    case VALID_BRAND_NAME.EGL_TOURS:
      return t('Brand_Live_View_Now')
    case VALID_BRAND_NAME.PXEC:
      return t('Common_AddToCart')
    default:
      return t('Common_BuyNow')
  }
}

const RoomProduct = (props: RoomProductProps) => {
  const {
    className,
    productInfo,
    productUrl = '',
    sortName,
    isLive,
    isTop,
    isDel,
    handleAddCartClick,
    handleBuyNowClick,
    observer,
    gtmItemEventParams,
    pipWithWinOpen,
    handlePromotionMessage,
    liveRoomId = '',
  } = props
  const { t } = useTranslation()

  const { productName, productDesc, images, tags, productId, minPrice, minLivePrice, minOrigPrice, currency } = productInfo

  const liveRoomContext: any = useContext(LiveRoomContext)

  const { brandName } = liveRoomContext

  const router = useRouter()
  const { locale } = router

  const { priceLabel, displayPrice, productPrice, productOriginalPrice } = getDisplayPrice({
    isLive,
    minPrice,
    livePrice: minLivePrice,
    origPrice: minOrigPrice,
  })
  const roomProductRef = useRef<any>()
  const isValidBrandName = Object.values(VALID_BRAND_NAME).includes(brandName)
  const { isDesktop } = useMedia()
  const commonData = useAppSelector((state) => state.common)
  const dittoCurrency = !isEmpty(commonData) && !isEmpty(commonData?.locationData) && commonData?.locationData?._dittoCurrency
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { mutate: countProductClickMutation, isLoading: isCountingProductClick } = useMutation(countProductClick)

  useObserve({ observer: observer, targetRef: roomProductRef.current })
  useLiveImpression({ ref: roomProductRef, type: LIVE_TRACK_EVENT.PRODUCT_IMPRESSION, roomId: liveRoomId, productId: Number(productId) || undefined })

  const triggerCountProductClick = (roomId: string, numericProductId: number) => {
    if (isCountingProductClick) return
    const uidFromQuery = searchParams.get('uid')
    countProductClickMutation({
      roomId,
      productId: numericProductId,
      productImage: images[0],
      productName,
      memberInfoId: uidFromQuery || '0',
    })
  }

  const handleBrandBuyNowClick = (event: any) => {
    event.stopPropagation()

    if (brandName === VALID_BRAND_NAME.PXEC) {
      const roomId = pathname.split('/').pop()
      let numericProductId = Number(productUrl.split('/').pop())
      if (isNaN(numericProductId)) {
        numericProductId = 10
      }
      if (!roomId || !numericProductId) return

      pxecPostMessages.buyNow(String(numericProductId), roomId)
      triggerCountProductClick(roomId, numericProductId)

      return
    }

    if (!isEmpty(productUrl)) {
      handlePromotionMessage({ productId, name: productName })

      if (isDesktop) {
        window.open(safeUrl(productUrl))
      } else {
        pipWithWinOpen(productUrl)
      }
    }
  }

  const handlePXECVisitProduct = (event: any) => {
    event.stopPropagation()

    if (brandName !== VALID_BRAND_NAME.PXEC) return

    const roomId = pathname.split('/').pop()
    const numericProductId = Number(productUrl.split('/').pop())
    if (!roomId || !numericProductId) return

    pxecPostMessages.visitProductDetail(String(numericProductId), roomId)
    triggerCountProductClick(roomId, numericProductId)
  }

  const handleExternalProductDataClick = (event: any) => {
    event.stopPropagation()

    if (brandName === VALID_BRAND_NAME.PXEC) {
      handleBrandBuyNowClick(event)
      return
    }

    if (!isEmpty(productUrl)) {
      handlePromotionMessage({ productId, name: productName })

      if (isDesktop) {
        window.open(safeUrl(productUrl))
      } else {
        pipWithWinOpen(productUrl)
      }
    }
  }

  const handleProductClick = (event: any) => {
    trackLiveEvent({ type: LIVE_TRACK_EVENT.PRODUCT_CLICK, roomId: liveRoomId, productId: Number(productId) || undefined })
    if (isClientSideShopApp) {
      const directUrl = encodeURIComponent(`${window.location.origin}/${locale}${ROUTE_PATH.PRODUCT}/${productId}`)

      window.location.href = `tutorshop://main?tab=store&openUrl=${directUrl}`
    } else {
      if (isValidBrandName) {
        if (brandName === VALID_BRAND_NAME.PXEC) {
          handlePXECVisitProduct(event)
          return
        }
        handleExternalProductDataClick(event)
      } else {
        handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.PRODUCT}/${productId}` })
      }
    }
    // 隱藏商品不送 select_item
    !isEmpty(gtmItemEventParams) &&
      !productInfo?.isHidden &&
      handleGtmSelectItem({
        ...(gtmItemEventParams as { itemListName: string; itemListId?: string }),
        productInfoList: [
          {
            ...(productInfo as unknown as ProductInfoType),
          },
        ],
      })
  }

  const canBuy = !isLive || (isLive && !isDel)

  const roomProductDataAttibute = {
    'data-id': productId,
  }

  const handleAddToCart = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    event.stopPropagation()

    if (!isLiveMallClub && dittoCurrency !== currency) {
      toast.error(`${t('Common_Status_No_Cross_Border_Delivery')}`)
      return
    }

    handleAddCartClick(event, productId)
  }

  const handleBuyNow = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    event.stopPropagation()

    if (!isLiveMallClub && dittoCurrency !== currency) {
      toast.error(`${t('Common_Status_No_Cross_Border_Delivery')}`)
      return
    }

    handleBuyNowClick(event, productId)
  }

  return (
    <div className={cx('product', className)} onClick={handleProductClick} ref={roomProductRef} {...roomProductDataAttibute}>
      <a className={cx('product-link')} href={`${ROUTE_PATH.PRODUCT}/${productId}`}></a>
      {images?.length > 0 && (
        <div className={cx('product-image')}>
          {isLiveMallClub ? (
            <Carousell images={images} tags={[]} handleClick={handleProductClick} />
          ) : (
            <Image src={images[0]} width={80} height={80} style={{ objectFit: 'contain' }} alt='product image' />
          )}
          <div className={cx('product-image-sort')}>
            {isTop && (
              <div className={cx('product-image-sort-top')}>
                <Icon.Pin />
              </div>
            )}
            <span className={cx('product-image-sort-name')}>{sortName}</span>
          </div>

          {!isEmpty(tags) && tags?.length > 0 && hasDoubleEleven(tags) && (
            <div className={cx('product-image__badge-double-11')}>
              <Image src='/images/badge-double-11.png' fill alt='' />
            </div>
          )}
        </div>
      )}

      <div className={cx('product-content')}>
        <p className={cx('product-content-name')}>{productName}</p>
        <p className={cx('product-content-description')}>{productDesc}</p>

        {!isLiveMallClub && tags?.length > 0 && (
          <div className={cx('product__tags')}>
            {tags?.map((item: ITagItem, index: number) => (
              <Tag key={index} style={item.style} name={item.name} />
            ))}
          </div>
        )}

        <div className={cx('product-content-action')}>
          <div className={cx('product-content-action-price', { isLiveMallClub })}>
            {!isLiveMallClub && <p className={cx('product-content-action-price-title')}>{t(priceLabel)}</p>}
            <p className={cx('product-content-action-price-number')}>{`${isLiveMallClub ? 'JPY ' : '$'}${displayPrice}`}</p>
            <DiscountRateTag productPrice={productPrice} productOriginalPrice={productOriginalPrice} />
          </div>

          {canBuy && !isValidBrandName && (
            <div className={cx('product-content-action-button')}>
              <ActionButton className={cx('product-content-action-button__cart')} onClick={handleAddToCart} theme='secondary'>
                {t('Common_AddToCart')}
              </ActionButton>
              <ActionButton className={cx('product-content-action-button__buy')} onClick={handleBuyNow}>
                {t('Common_BuyNow')}
              </ActionButton>
            </div>
          )}

          {!canBuy && !isValidBrandName && <div className={cx('product-content-action-off')}> {t('LiveRoom_Removed')}</div>}

          {isValidBrandName && (
            <Button className={cx('product-content-action-off')} onClick={handleBrandBuyNowClick} size='sm'>
              {getBrandButtonText(brandName, t)}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default RoomProduct
